import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { Col, Input, Label, Row, Spinner } from 'reactstrap';

import Button from 'components/App/Button';
import IconStarDiscovery from 'components/App/IconStarDiscovery';
import ImagePreview from 'components/App/ImagePreview';
import Pagination from 'components/Pagination';

import { ReactComponent as IconThumbDown } from 'assets/images/thumb-down.svg';
import { ReactComponent as IconThumbUp } from 'assets/images/thumb-up.svg';

import { Avatar, DatePicker } from 'antd';
import { themeColor } from 'constants/index';
import moment from 'moment';
import cn from 'utils/classnames';
import { getRatingAverageText, getRatingText } from 'utils';
import styles from './DiscoveryPlaceReviews.module.scss';

function DiscoveryPlaceReviews({
  overallRating,
  totalReviews,
  ratingBreakdown,
  reviews = [],
  onSearch,
  onReset,
  onDeleteReply,
  currentPage = 1,
  totalPages = 5,
  onPageChange,
  loading,
}) {
  const mainContentRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const handleSearch = () => {
    if (onSearch) {
      onSearch({
        query: searchQuery,
        fromDate,
        toDate,
      });
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setFromDate(null);
    setToDate(null);

    if (onReset) {
      onReset();
    }
  };

  const handlePageChange = ({ selected }) => {
    if (onPageChange) {
      onPageChange(selected + 1);
    }
  };

  return (
    <div className={styles.Component}>
      <div className={styles.Container}>
        <aside className={styles.Sidebar}>
          <div className={styles.RatingSummary}>
            <span className={styles.RatingHeader}>
              <span className={styles.OverallRating}>{overallRating}</span>
              <span className={styles.OverallRatingDivider}>/5</span>
            </span>
            <p className={styles.RatingDescription}>
              {
                totalReviews > 0 ? (
                  <>
                    <span>{getRatingAverageText(overallRating)}</span>
                    <span>-</span>
                    <span>
                      {totalReviews}
                      {' '}
                      đánh giá
                    </span>
                  </>
                ) : (<span>Chưa có đánh giá</span>)
              }

            </p>
          </div>

          <div className={styles.RatingBreakdown}>
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className={styles.RatingItem}>
                <div className={styles.RatingLabel}>
                  <span>{getRatingText(star)}</span>
                  <IconStarDiscovery star={star} size={16} />
                </div>
                <span className={styles.RatingCount}>
                  {ratingBreakdown[star] || 0}
                </span>
              </div>
            ))}
          </div>
        </aside>

        <main className={styles.MainContent} ref={mainContentRef}>
          <div className={styles.SearchSection}>
            <h2 className={styles.SearchTitle}>
              Lượt đánh giá
            </h2>

            <Row>
              <Col md="4" className="mb-24" lg="12" xl="4">
                <Label>
                  Tìm kiếm
                </Label>
                <div className="position-relative">
                  <Input
                    type="text"
                    placeholder="Tìm theo tên user"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={styles.SearchInput}
                    style={{
                      paddingLeft: '36px',
                      height: '39px',
                      width: '100%',
                      minWidth: 'unset',
                    }}
                  />

                  <i
                    className="fas fa-search"
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#CED4DA',
                    }}
                  />
                </div>
              </Col>

              <Col md="4" className="mb-24" lg="12" xl="4">
                <Label>
                  Từ ngày
                </Label>

                <DatePicker
                  className="custom_date_picker"
                  placeholder="Từ ngày"
                  inputReadOnly
                  onChange={(date) => setFromDate(date)}
                />
              </Col>

              <Col md="4" className="mb-24" lg="12" xl="4">
                <Label>
                  Đến ngày
                </Label>
                <DatePicker
                  className="custom_date_picker"
                  placeholder="Đến ngày"
                  inputReadOnly
                  disabledDate={(current) => current
                    && current.valueOf() < Date.parse(fromDate)}
                  onChange={(date) => setToDate(date)}
                />
              </Col>
            </Row>

            <div className="d-flex gap-4 mb-24">
              <Button
                classes={styles.ResetButton}
                onClick={handleReset}
                color={themeColor.SECONDARY}
              >
                Nhập lại
              </Button>

              <Button
                classes={styles.SearchButton}
                onClick={handleSearch}
              >
                Tìm kiếm
              </Button>
            </div>

            <div className={cn(styles.ReviewsList, 'position-relative')}>
              {loading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  zIndex: 10,
                }}
                >
                  <Spinner />
                </div>
              )}

              {reviews.map((review) => (
                <div key={review.id} className={styles.ReviewCard}>
                  <div className={styles.ReviewHeader}>
                    <div className={styles.ReviewerInfo}>
                      <div className={styles.ReviewerAvatar}>
                        <Avatar
                          src="https://placehold.co/32x32"
                          size={32}
                          round
                        >
                          {review.userName.charAt(0).toUpperCase()}
                          {review.userName.charAt(1).toUpperCase()}
                        </Avatar>
                        <span className={styles.ReviewerName}>
                          {review.userName}
                        </span>
                      </div>

                      <div className={styles.ReviewRating}>
                        <span className={styles.RatingText}>
                          {getRatingText(review.star)}
                        </span>
                        <IconStarDiscovery star={review.star} size={12} />
                      </div>
                    </div>
                  </div>

                  <div className={styles.ReviewContent}>
                    <div className={styles.ReviewTextWrapper}>
                      <h3 className={styles.ReviewTitle}>
                        {review.title}
                      </h3>

                      <p className={styles.ReviewDescription}>
                        {review.content}
                      </p>
                    </div>

                    {review.images && review.images.length > 0 && (
                      <ImagePreview images={review.images} />
                    )}
                  </div>

                  <div className={styles.ReviewFooter}>
                    <span className={styles.VisitDate}>
                      Ngày đến thăm:&nbsp;
                      <span className={styles.VisitDateValue}>
                        {moment(review.visitDate).format('[tháng] M [năm] YYYY')}
                      </span>
                    </span>
                  </div>

                  <div className={styles.ReviewActions}>
                    <div className={styles.Reactions}>
                      <div className={styles.ReactionItem}>
                        <IconThumbUp width={16} height={16} />
                        <span>{review.likes || 0}</span>
                      </div>
                      <div className={styles.ReactionItem}>
                        <IconThumbDown width={16} height={16} />
                        <span>{review.dislikes || 0}</span>
                      </div>
                    </div>

                    <Button
                      onClick={() => onDeleteReply && onDeleteReply(review.id, review)}
                      color={themeColor.DANGER}
                      classes={styles.DeleteButton}
                    >
                      {review.deletedAt ? 'Khôi phục' : 'Xoá'}
                      {' '}
                      phản hồi
                    </Button>
                  </div>
                </div>
              ))}

              {reviews.length === 0 && (
                <div className={styles.EmptyState}>
                  <p>Không tìm thấy đánh giá</p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className={styles.PaginationWrapper}>
                <Pagination
                  page={currentPage}
                  pageCount={totalPages}
                  onChangePage={(selected) => {
                    handlePageChange(selected);

                    mainContentRef.current.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

DiscoveryPlaceReviews.propTypes = {
  overallRating: PropTypes.number,
  totalReviews: PropTypes.number,
  ratingBreakdown: PropTypes.shape({
    5: PropTypes.number,
    4: PropTypes.number,
    3: PropTypes.number,
    2: PropTypes.number,
    1: PropTypes.number,
  }),
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      userName: PropTypes.string,
      star: PropTypes.number,
      title: PropTypes.string,
      content: PropTypes.string,
      images: PropTypes.arrayOf(PropTypes.string),
      visitDate: PropTypes.string,
      likes: PropTypes.number,
      dislikes: PropTypes.number,
    }),
  ),
  onSearch: PropTypes.func,
  onReset: PropTypes.func,
  onDeleteReply: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func,
  loading: PropTypes.bool,
};

DiscoveryPlaceReviews.defaultProps = {
  overallRating: 9.5,
  totalReviews: 254,
  ratingBreakdown: {
    5: 230,
    4: 20,
    3: 3,
    2: 1,
    1: 0,
  },
  reviews: [],
  onSearch: null,
  onReset: null,
  onDeleteReply: null,
  currentPage: 1,
  totalPages: 5,
  onPageChange: null,
  loading: false,
};

export default DiscoveryPlaceReviews;
